import { Controller } from "@hotwired/stimulus"

// Dashboard Customization Controller
// Allows users to show/hide dashboard widgets and save preferences
export default class extends Controller {
  static targets = ["widget", "toggle", "customizePanel", "saveButton", "cancelButton"]
  static values = {
    saveUrl: String,
    userId: Number,
    userType: String  // 'member' or 'admin_user'
  }

  connect() {
    // Load preferences from data attributes
    this.preferences = this.loadPreferences()
    this.originalPreferences = { ...this.preferences }
    this.applyPreferences()
  }

  // Load preferences from hidden input or data attribute
  loadPreferences() {
    const prefsElement = document.getElementById('dashboard-preferences-data')
    if (prefsElement) {
      try {
        return JSON.parse(prefsElement.value || '{}')
      } catch (e) {
        console.error('Failed to parse dashboard preferences:', e)
        return {}
      }
    }
    return {}
  }

  // Apply current preferences to widgets
  applyPreferences() {
    this.widgetTargets.forEach(widget => {
      const widgetId = widget.dataset.widgetId
      const isHidden = this.preferences[widgetId] === false

      if (isHidden) {
        widget.classList.add('hidden')
      } else {
        widget.classList.remove('hidden')
      }

      // Update toggle checkbox state
      const toggle = this.toggleTargets.find(t => t.dataset.widgetId === widgetId)
      if (toggle) {
        toggle.checked = !isHidden
      }
    })
  }

  // Toggle customize panel
  toggleCustomizePanel() {
    if (this.hasCustomizePanelTarget) {
      this.customizePanelTarget.classList.toggle('hidden')
    }
  }

  // Open customize panel
  openCustomizePanel() {
    if (this.hasCustomizePanelTarget) {
      this.customizePanelTarget.classList.remove('hidden')
    }
  }

  // Close customize panel without saving
  closeCustomizePanel() {
    if (this.hasCustomizePanelTarget) {
      this.customizePanelTarget.classList.add('hidden')
      // Restore original preferences
      this.preferences = { ...this.originalPreferences }
      this.applyPreferences()
    }
  }

  // Toggle widget visibility (preview)
  toggleWidget(event) {
    const toggle = event.currentTarget
    const widgetId = toggle.dataset.widgetId
    const isChecked = toggle.checked

    // Update preferences
    this.preferences[widgetId] = isChecked

    // Find and toggle the widget
    const widget = this.widgetTargets.find(w => w.dataset.widgetId === widgetId)
    if (widget) {
      if (isChecked) {
        widget.classList.remove('hidden')
      } else {
        widget.classList.add('hidden')
      }
    }

    // Enable save button
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  // Save preferences to server
  async savePreferences() {
    if (!this.saveUrlValue) {
      console.error('No save URL provided')
      return
    }

    // Disable save button during request
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.innerHTML = '<svg class="animate-spin h-4 w-4 inline mr-2" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Saving...'
    }

    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          dashboard_preferences: this.preferences
        })
      })

      if (response.ok) {
        // Update original preferences
        this.originalPreferences = { ...this.preferences }

        // Show success message
        this.showNotification('Dashboard customization saved!', 'success')

        // Close panel
        this.closeCustomizePanel()

        // Reset save button
        if (this.hasSaveButtonTarget) {
          this.saveButtonTarget.innerHTML = 'Save Changes'
          this.saveButtonTarget.disabled = false
        }
      } else {
        throw new Error('Failed to save preferences')
      }
    } catch (error) {
      console.error('Error saving preferences:', error)
      this.showNotification('Failed to save preferences. Please try again.', 'error')

      // Re-enable save button
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.innerHTML = 'Save Changes'
        this.saveButtonTarget.disabled = false
      }
    }
  }

  // Reset to default preferences
  resetToDefaults() {
    // Enable all widgets
    this.preferences = {}
    this.widgetTargets.forEach(widget => {
      const widgetId = widget.dataset.widgetId
      this.preferences[widgetId] = true
    })

    this.applyPreferences()

    // Enable save button
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  // Get CSRF token
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  // Show notification
  showNotification(message, type = 'info') {
    // Look for existing notification area or create one
    let notification = document.getElementById('dashboard-notification')

    if (!notification) {
      notification = document.createElement('div')
      notification.id = 'dashboard-notification'
      notification.className = 'fixed top-4 right-4 z-50 max-w-md'
      document.body.appendChild(notification)
    }

    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'

    notification.innerHTML = `
      <div class="${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in">
        ${type === 'success' ? `
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ` : type === 'error' ? `
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ` : ''}
        <span class="font-medium">${message}</span>
      </div>
    `

    // Auto-remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('animate-fade-out')
      setTimeout(() => {
        notification.remove()
      }, 300)
    }, 3000)
  }
}
